import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import useHeader from '@hooks/useHeader';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardMedia,
    Stack,
    Typography,
} from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { emailScheduledQueryOptions } from '@utils/queryOptions';
import { useTranslation } from 'react-i18next';
import { useSchesuledEmailOptions } from './useSchesuledEmailOptions';
import { CancelScheduleSendRounded } from '@mui/icons-material';
import EmailContent from '@Components/Specific/EmailContent';

const EmailScheduledDetails = () => {
    const { t } = useTranslation();
    const { scheduledId } = useParams({});
    const navigate = useNavigate();

    const { data: email } = useSuspenseQuery(
        emailScheduledQueryOptions(scheduledId),
    );

    useHeader({
        title: t('scheduledEmail'),
    });

    const { reschedule, unscheduleMutation, duplicate, deleteEmail } =
        useSchesuledEmailOptions()(email);

    const { mutateAsync: unschedule, isPending: isUnscheduling } =
        unscheduleMutation;

    const quickOptions: QuickOption[] = [reschedule, duplicate, deleteEmail];

    return (
        <Stack spacing={2}>
            <Card>
                <CardContent sx={{ width: '100%' }}>
                    <Stack direction="row" spacing={1}>
                        <CardMedia
                            component="img"
                            src={email.thumbnail}
                            alt="thumbnail"
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: 4,
                                bgcolor: email.background_color,
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                px: 2,
                                height: '100%',
                                flexGrow: 1,
                            }}
                        >
                            <Box>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                    }}
                                >
                                    <Typography variant="headlineLarge">
                                        {email.subject}
                                    </Typography>
                                    <QuickOptions options={quickOptions} />
                                </Box>
                                <Typography
                                    variant="headlineSmall"
                                    gutterBottom
                                >
                                    {email.preview_text}
                                </Typography>
                                <Typography variant="bodyLarge" gutterBottom>
                                    {email.description}
                                </Typography>
                                <Typography variant="bodyLarge">
                                    {t('Scheduled')}:{' '}
                                    {email.schedule_time.toContextualDateTime()}
                                </Typography>
                            </Box>
                            <Stack
                                direction="row"
                                spacing={2}
                                mt={2}
                                // justifyContent="flex-end"
                            >
                                <LoadingButton
                                    variant="tonal"
                                    color="secondary"
                                    size="large"
                                    onClick={() =>
                                        unschedule(scheduledId).then(() =>
                                            navigate({
                                                to: `/emails/drafts/${scheduledId}`,
                                            }),
                                        )
                                    }
                                    loading={isUnscheduling}
                                    endIcon={<CancelScheduleSendRounded />}
                                >
                                    {t('Cancel Send')}
                                </LoadingButton>
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>
            <EmailContent htmlContent={email.body} />
        </Stack>
    );
};

export default EmailScheduledDetails;
